<script>
import {
  ThreadStatusEnum,
  TicketCategoryStatusEnum,
  EndUserTicketCategoryStatusEnum,
} from "@/utils/constants.js";

export const threadStatusToEndUserStatusMapping = Object.freeze({
  [ThreadStatusEnum.NEW]: EndUserTicketCategoryStatusEnum.OPEN,
  [ThreadStatusEnum.OPEN]: EndUserTicketCategoryStatusEnum.OPEN,
  [ThreadStatusEnum.PENDING_USER_REPLY]:
    EndUserTicketCategoryStatusEnum.AWAITING_YOUR_REPLY,
  [ThreadStatusEnum.ONHOLD]: EndUserTicketCategoryStatusEnum.OPEN,
  [ThreadStatusEnum.USER_CLOSED]: EndUserTicketCategoryStatusEnum.CLOSED_BY_YOU,
  [ThreadStatusEnum.SUPPORT_CLOSED]:
    EndUserTicketCategoryStatusEnum.CLOSED_BY_SUPPORT,
  [ThreadStatusEnum.AUTO_CLOSED]:
    EndUserTicketCategoryStatusEnum.CLOSED_BY_SUPPORT,
  [ThreadStatusEnum.ESCALATED]: EndUserTicketCategoryStatusEnum.OPEN,
  [ThreadStatusEnum.REOPENED]: EndUserTicketCategoryStatusEnum.OPEN,
});

export const threadStatusToEndUserStatusThemeMap = Object.freeze({
  [ThreadStatusEnum.AI_ONGOING]: "green",
  [ThreadStatusEnum.NEW]: "yellow",
  [ThreadStatusEnum.OPEN]: "yellow",
  [ThreadStatusEnum.PENDING_USER_REPLY]: "yellow",
  [ThreadStatusEnum.ONHOLD]: "yellow",
  [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: "red",
  [ThreadStatusEnum.USER_CLOSED]: "green",
  [ThreadStatusEnum.SUPPORT_CLOSED]: "green",
  [ThreadStatusEnum.AUTO_CLOSED]: "green",
  [ThreadStatusEnum.SOFT_ALERT]: "yellow",
  [ThreadStatusEnum.AI_CLOSED]: "green",
  [ThreadStatusEnum.ESCALATED]: "yellow",
  [ThreadStatusEnum.REOPENED]: "yellow",
  [ThreadStatusEnum.UNKNOWN]: "blue",
});

// Leave it like this just incase we need to add more options
const ticketStatusDropdownOptions = Object.freeze({
  [ThreadStatusEnum.NEW]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.OPEN]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.PENDING_USER_REPLY]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.ONHOLD]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.ESCALATED]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.REOPENED]: [
    { text: "Close Ticket", value: ThreadStatusEnum.USER_CLOSED },
  ],
  [ThreadStatusEnum.USER_CLOSED]: [
    { text: "Reopen Ticket", value: ThreadStatusEnum.REOPENED },
  ],
});
</script>

<script setup>
import { threadStatusIconHollowMap } from "./ThreadStatusDropdown2.vue";

const props = defineProps({
  status: {
    type: String,
    default: null,
  },
  allowToOpen: {
    type: Boolean,
    default: true,
  },
  paddingSize: {
    type: String,
    default: "normal",
  },
  threadDetails: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["update-status", "convert-to-ticket"]);

const dropdownRef = ref();
const selected = ref(props.status);
const isModalVisible = ref(false);

// Need to compute the options based on the current status

watch(
  () => props.status,
  (val) => {
    selected.value = val;
    console.log("selected.value: ", selected.value);
  }
);

const onStatusClick = (option) => {
  console.log("option.value: ", option.value);
  const ticketStatuesArray = Object.values(TicketCategoryStatusEnum);

  // if converting non-ticket status to ticket status, emit convert-to-ticket event
  if (
    !ticketStatuesArray.includes(props.status) &&
    ticketStatuesArray.includes(option.value)
  ) {
    emit("convert-to-ticket", option.value);
  } else {
    emit("update-status", option.value);
  }

  isModalVisible.value = false;
};

const dropdownOptions = computed(
  () => ticketStatusDropdownOptions[selected.value] || []
);
const theme = computed(
  () => threadStatusToEndUserStatusThemeMap[selected.value] || null
);
const label = computed(
  () => threadStatusToEndUserStatusMapping[selected.value] || selected.value
);
const isHollow = computed(() => threadStatusIconHollowMap[selected.value] || false)
</script>

<template>
  <dropdown
    ref="dropdownRef"
    class="status-dropdown"
    :class="{
      [theme]: theme,
      'is-visible': isModalVisible,
    }"
    :label="label"
    header="Status"
    :show-header="false"
    v-model="selected"
    :options="dropdownOptions"
    :allowToOpen="allowToOpen"
    :padding-size="paddingSize"
    :showSearch="false"
    v-model:visibility="isModalVisible"
    v-b-tooltip="{ title: label, trigger: 'hover' }"
  >
    <template #pre-label-icon>
      <IconCircleFill class="label-icon" :isHollow="isHollow" />
    </template>

    <div v-for="option in dropdownRef?.sortedOptions">
      <div
        class="dropdown-item d-flex align-items-center"
        :class="{ active: selected === option.value }"
        @click.stop="onStatusClick(option)"
      >
        <IconCircleFill
          :class="threadStatusToEndUserStatusThemeMap[option.value]"
          :isHollow="threadStatusIconHollowMap[option.value] || false"
        />
        <div
          class="d-flex align-items-center justify-content-between flex-grow-1"
        >
          <span class="option-label">{{ option.text }}</span>
          <IconCheck v-if="selected === option.value" />
        </div>
      </div>
    </div>
  </dropdown>
</template>

<style lang="scss" scoped>
.status-dropdown {
  stroke-width: 3px;
  :deep(.dropdown-menu) {
    width: 250px;


    .dropdown-item {
      .circle-icon {
        &.red {
          border: $red-200;
          fill: $red-200;
        }

        &.green {
          fill: $green-150;
        }

        &.yellow {
          fill: $yellow-250;
        }

        &.blue {
          fill: var(--primary-250);
        }

        &.gray {
          fill: $neutral-250;
        }
      }
    }
  }

  .option-label {
    margin-left: 0.75rem;
  }

  &.is-visible {
    &.red {
      :deep(.btn) {
        background-color: $red-50;
        border: 1px solid $red-100;
      }
    }

    &.gray {
      :deep(.btn) {
        background-color: $neutral-150;
        border: 1px solid $neutral-300;
      }
    }

    &.green {
      :deep(.btn) {
        background-color: $neutral-150;
        border: 1px solid $neutral-300;
      }
    }

    &.yellow {
      :deep(.btn) {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
    }

    &.blue {
      :deep(.btn) {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
    }
  }

  &.red {
    :deep(.btn) {
      background-color: $red-0;
      border: 1px solid $red-50;

      &:hover {
        border: 1px solid $red-100;
      }
      &.show {
        background-color: $red-50;
        border: 1px solid $red-150;
      }

      .label-icon {
        fill: $red-200;
        stroke: $red-200;
      }
    }
  }

  &.green {
    :deep(.btn) {
      background-color: $green-0;
      border: 1px solid $green-50;

      &:hover {
        border: 1px solid $green-100;
      }
      &.show {
        background-color: $green-50;
        border: 1px solid $green-150;
      }
      .label-icon {
        fill: $green-150;
        stroke: $green-150;
      }
    }
  }

  &.yellow {
    :deep(.btn) {
      background-color: $yellow-0;
      border: 1px solid $yellow-50;
      &:hover {
        border: 1px solid $yellow-100;
      }
      &.show {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
      .label-icon {
        fill: $yellow-250;
        stroke: $yellow-250;
      }
    }
  }

  &.blue {
    :deep(.btn) {
      background-color: var(--primary-0);
      border: 1px solid var(--primary-50);
      &:hover {
        border: 1px solid var(--primary-100);
      }
      &.show {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
      .label-icon {
        fill: var(--primary-250);
        stroke: var(--primary-250);
      }
    }
  }

  &.gray {
    :deep(.btn) {
      background-color: $neutral-50;
      border: 1px solid $neutral-150;
      &:hover {
        border: 1px solid $neutral-150;
      }
      &.show {
        background-color: $neutral-100;
        border: 1px solid $neutral-250;
      }
      .label-icon {
        fill: $neutral-250;
        stroke: $neutral-250;
      }
    }
  }

  &.active {
    background: var(--primary-0);
    border: 1px solid var(--primary-50);
  }
}
</style>
